import { Button } from '@cobliteam/ui/components';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import type { Avatar } from '@cobliteam/ui-business/components/Avatar';
import type { UIMessage } from 'ai';
import type { ComponentProps, HTMLAttributes } from 'react';

export type MessageProps = HTMLAttributes<HTMLDivElement> & {
  from: UIMessage['role'];
  hideAvatar?: boolean;
};

export const Message = ({ children, className, from, hideAvatar = false, ...props }: MessageProps) => (
  <div className={cn('group flex w-full items-end justify-end gap-2', from === 'user' ? 'is-user' : 'is-assistant flex-row-reverse justify-end', className)} {...props}>
    {!hideAvatar && <MessageAvatar role={from} />}
    {children}
  </div>
);

export type MessageContentProps = HTMLAttributes<HTMLDivElement>;

export const MessageContent = ({ children, className, ...props }: MessageContentProps) => (
  <div
    className={cn(
      'flex flex-col gap-2 overflow-hidden rounded-lg border border-gray-4 px-3 py-1.5 text-gray-12 text-sm',
      'group-[.is-user]:mt-4 group-[.is-user]:bg-gray-4 group-[.is-user]:text-gray-12',
      'group-[.is-assistant]:w-full group-[.is-assistant]:text-gray-12',
      'group-[.is-user]:border-gray-5',
      'group-[.is-assistant]:border-none',
      className
    )}
    {...props}
  >
    {children}
  </div>
);

export type MessageAvatarProps = ComponentProps<typeof Avatar> & {
  name?: string;
  loading?: boolean;
  role: UIMessage['role'];
  lastMessage?: boolean;
};

export const MessageAvatar = ({ role, loading = false, className }: MessageAvatarProps) => (
  <div className='flex-full flex-row'>
    <div
      className={cn(
        'mt-2 flex size-6 items-center justify-center rounded-full p-4',
        !loading && 'mt-2 border-2 border-gray-8',
        role === 'assistant' && 'border-gray-6',
        role === 'user' && 'border-gray-6 bg-gray-4',
        className
      )}
    >
      {loading ? <Icon name='loader-circle' size='sm' color='gray-dark' className='animate-spin' aria-hidden='true' /> : null}
      {!loading && (
        <Tooltip content={role === 'assistant' ? 'Cobli IA' : 'Você'}>
          <Icon name={role === 'assistant' ? 'sparkles' : 'user'} size='md' color='gray' aria-hidden='true' className='flex-shrink-0' />
        </Tooltip>
      )}
    </div>
  </div>
);

export type MessageActionsProps = ComponentProps<'div'>;

export const MessageActions = ({ className, children, ...props }: MessageActionsProps) => (
  <div className={cn('flex items-center justify-end gap-1', className)} {...props}>
    {children}
  </div>
);

export type MessageActionProps = ComponentProps<typeof Button> & {
  tooltip?: string;
  label?: string;
};

export const MessageAction = ({ tooltip, children, label, variant = 'ghost', size = 'sm', ...props }: MessageActionProps) => {
  const button = (
    <Button size={size} type='button' variant={variant} {...props}>
      {children}
      <span className='sr-only'>{label || tooltip}</span>
    </Button>
  );

  if (tooltip) {
    return <Tooltip content={tooltip}>{button}</Tooltip>;
  }

  return button;
};

export type MessageToolbarProps = ComponentProps<'div'>;

export const MessageToolbar = ({ className, children, ...props }: MessageToolbarProps) => (
  <div className={cn('mt-4 flex w-full flex-1 items-center justify-end gap-4', className)} {...props}>
    {children}
  </div>
);
